import React, { useState, useMemo, useRef } from 'react';
import { PlaylistAdd, Check } from '@mui/icons-material';

const AddAllToWatchlistButton = ({
  symbols = [],
  existingWatchlistSymbols = [],
  onAddToWatchList,
  onNoteChange,
  onBatchAddComplete,
  userEmail,
  listName = '',
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [totalToAdd, setTotalToAdd] = useState(0);

  // Ref to store the snapshot of symbols to add (prevents recalculation during operation)
  const symbolsToAddRef = useRef([]);

  // Calculate symbols that aren't already in watchlist (for display only, not during operation)
  const newSymbols = useMemo(() => {
    if (isAdding) {
      // During adding, don't recalculate - use the snapshot
      return symbolsToAddRef.current;
    }
    const existingSet = new Set(existingWatchlistSymbols.filter(s => s && typeof s === 'string').map(s => s.toUpperCase()));
    // Deduplicate symbols
    const seen = new Set();
    return symbols.filter(s => {
      if (!s || typeof s !== 'string') return false; // Filter out null/undefined/non-strings
      const upper = s.toUpperCase();
      if (existingSet.has(upper) || seen.has(upper)) {
        return false;
      }
      seen.add(upper);
      return true;
    });
  }, [symbols, existingWatchlistSymbols, isAdding]);

  const allAlreadyAdded = !isAdding && newSymbols.length === 0 && symbols.length > 0;

  const getPillStyles = (isDisabled, isComplete) => {
    if (isComplete) {
      return 'bg-green-500 text-white shadow-sm';
    }
    if (isDisabled) {
      return 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-sm';
    }
    if (!userEmail) {
      // Logged out state - interactive
      return 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 shadow-sm';
    }
    return 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700 shadow-sm';
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAdding || newSymbols.length === 0) return;

    // IMPORTANT: Capture snapshot of symbols to add BEFORE starting
    // This prevents the list from changing as we add symbols
    const existingSet = new Set(existingWatchlistSymbols.filter(s => s && typeof s === 'string').map(s => s.toUpperCase()));

    // Deduplicate and filter out existing symbols
    const seen = new Set();
    const symbolsSnapshot = symbols.filter(s => {
      if (!s || typeof s !== 'string') return false; // Filter out null/undefined/non-strings
      const upper = s.toUpperCase();
      if (existingSet.has(upper) || seen.has(upper)) {
        return false;
      }
      seen.add(upper);
      return true;
    });

    // Store in ref so useMemo can return stable value during operation
    symbolsToAddRef.current = symbolsSnapshot;
    setTotalToAdd(symbolsSnapshot.length);
    setIsAdding(true);
    setAddedCount(0);

    try {
      // Format the date for the note
      const dateStr = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const sourceNote = listName
        ? `Added from "${listName}" on ${dateStr}`
        : `Added from list on ${dateStr}`;

      // Add symbols one by one using the SNAPSHOT (not newSymbols which can change)
      for (let i = 0; i < symbolsSnapshot.length; i++) {
        // Pass batch options to skip per-item server sync
        await onAddToWatchList(symbolsSnapshot[i], {
          skipServerSync: true,
          isBatchOperation: true
        });
        // Add note indicating the source list
        if (onNoteChange) {
          onNoteChange(symbolsSnapshot[i], sourceNote);
        }
        setAddedCount(i + 1);
      }

      // Batch complete - trigger single server sync
      if (onBatchAddComplete) {
        await onBatchAddComplete();
      }
    } catch (error) {
      console.error('[AddAllToWatchlist] Error adding symbols:', error);
    } finally {
      // Keep the "added" state visible briefly
      setTimeout(() => {
        setIsAdding(false);
        setAddedCount(0);
        setTotalToAdd(0);
        symbolsToAddRef.current = [];
      }, 2000);
    }
  };

  const pillBase = 'inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer';

  // Don't render if no symbols to show
  if (symbols.length === 0) return null;


  // All symbols already in watchlist
  if (allAlreadyAdded) {
    return (
      <button
        type="button"
        className={`${pillBase} ${getPillStyles(true, false)} ${className}`}
        disabled
        title="All symbols already in your Watch List"
      >
        <Check style={{ fontSize: 14 }} />
        <span>All in Watch List</span>
      </button>
    );
  }

  // Currently adding
  if (isAdding) {
    return (
      <button
        type="button"
        className={`${pillBase} ${getPillStyles(false, addedCount === totalToAdd)} ${className}`}
        disabled
      >
        <PlaylistAdd style={{ fontSize: 14 }} className="animate-pulse" />
        <span>
          {addedCount === totalToAdd
            ? `Added ${addedCount} symbols`
            : `Adding ${addedCount}/${totalToAdd}...`}
        </span>
      </button>
    );
  }

  // Ready to add
  return (
    <button
      type="button"
      className={`${pillBase} ${getPillStyles(false, false)} ${className}`}
      title={`Add all ${newSymbols.length} symbols to your Watch List`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PlaylistAdd style={{ fontSize: 14 }} />
      {isHovered && <span>Add all {newSymbols.length} to Watch List</span>}
    </button>
  );
};

export default AddAllToWatchlistButton;
